var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import Modal from '@rad/components/ui/modal';
import TypeAhead from '@rad/components/inputs/type-ahead';
import CheckSwitch from '@rad/components/inputs/checkswitch';
import { Tabs } from '@rad/components/ui/tabs';
import { usePromise } from '@rad/hooks';
import { createRoleState } from '../../../types/role';
import { createRole, getRolePermissions, updateRolePermissions } from '../../../common/network/role';
import { getRoleKinds, updateRoleKinds } from '../../../common/network/moderation';
import { getDiscussionKinds } from '../../../common/network/discussion-kind';
import { withDone } from '@rad/utility/http';
import { useAuth } from '../../../hooks/useAuth';
import PermissionForm from './permission-form';
import { expand } from '@rad/utility/validation';
import { useToast } from '../../../contexts/toast';
const ManageRoleModal = ({ isOpen, onClose, onSaved, rolesPromise, permissionsPromise }) => {
    const toast = useToast();
    const [auth] = useAuth();
    const canManageKinds = auth.hasPermission(['kind-access-read']);
    const [selection, setSelection] = React.useState();
    const [form, setForm] = React.useState(createRoleState());
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [kindsPromise] = React.useState(() => canManageKinds ? withDone(getDiscussionKinds())() : Promise.resolve([]));
    const allKinds = usePromise(kindsPromise);
    const isEditing = selection && selection[0];
    const handleSelection = (val) => {
        setSelection(val);
        setErrors({});
        if (val[0]) {
            setLoading(true);
            const promises = [
                getRolePermissions(val[1].name),
                canManageKinds ? getRoleKinds(val[1].name) : Promise.resolve([]),
            ];
            Promise.all(promises).then(([perms, kinds]) => {
                setForm({ name: val[1].name, permissions: perms, kinds });
                setLoading(false);
            });
        }
        else {
            setForm({ name: val[1], permissions: [], kinds: [] });
        }
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!form.name)
            return;
        setErrors({});
        const [success, err] = isEditing
            ? yield updateRolePermissions(form)
            : yield createRole(form);
        if (success) {
            if (canManageKinds) {
                yield updateRoleKinds(form.name, form.kinds);
            }
            onSaved();
            handleClose();
            toast.success(isEditing ? 'Role updated' : 'Role created');
        }
        else {
            const [, expanded] = expand(err);
            setErrors(expanded);
            toast.error('Please fix the errors below');
        }
    });
    const handleClose = () => {
        onClose();
        setSelection(undefined);
        setForm(createRoleState());
        setErrors({});
    };
    const toggleKind = (kind) => {
        setForm(prev => (Object.assign(Object.assign({}, prev), { kinds: prev.kinds.includes(kind)
                ? prev.kinds.filter(k => k !== kind)
                : [...prev.kinds, kind] })));
    };
    return (_jsx(Modal, { isOpen: isOpen, title: "Manage Roles", onClose: handleClose, onSave: handleSave, children: _jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(TypeAhead, { canAddNew: true, options: rolesPromise, optionKey: r => r.name, optionLabel: r => r.name, value: selection, onChange: handleSelection, label: "Select or create a role" }), errors.form && _jsx("div", { className: "alert alert-danger", children: errors.form.join(', ') }), loading ? (_jsx("div", { className: "text-center py-3", children: _jsx("div", { className: "spinner-border spinner-border-sm" }) })) : selection ? (canManageKinds ? (_jsxs(Tabs, { children: [_jsx(Tabs.Tab, { label: "Site Permissions", default: true, children: _jsx(PermissionForm, { permissionsPromise: permissionsPromise, selectedPermissions: form.permissions, onChange: perms => setForm(Object.assign(Object.assign({}, form), { permissions: perms })) }) }), _jsx(Tabs.Tab, { label: "Discussion Access", children: allKinds ? (_jsxs("div", { className: "d-flex flex-column gap-2", children: [_jsx("p", { className: "text-muted small mb-1", children: "Select which discussion types this role can access." }), allKinds.map(kind => (_jsx(CheckSwitch, { switch: false, label: kind.display, value: form.kinds.includes(kind.name), onChange: () => toggleKind(kind.name) }, kind.name))), allKinds.length === 0 && (_jsx("p", { className: "text-muted small", children: "No discussion kinds configured." }))] })) : (_jsx("div", { className: "text-center py-3", children: _jsx("div", { className: "spinner-border spinner-border-sm" }) })) })] })) : (_jsx(PermissionForm, { permissionsPromise: permissionsPromise, selectedPermissions: form.permissions, onChange: perms => setForm(Object.assign(Object.assign({}, form), { permissions: perms })) }))) : null] }) }));
};
export default ManageRoleModal;
